import { useEffect, useState } from "react";
import { Button, Input, Stack, useToast } from "@salesapp/ui";
import { crmApi, type Contact, type DuplicateMatch, type Lead, type LeadInput } from "../../lib/crmApi";
import { reportSaveBlocked, reportSaveFailure } from "../../lib/saveNotice";
import {
  FIELD_HINTS,
  LEAD_SOURCE_OPTIONS,
  LEAD_TEMPERATURE_OPTIONS,
} from "../../help/fieldHints";
import { CompanyLookup } from "./CompanyLookup";
import { FieldLabel } from "./FieldLabel";
import { ProductPicker } from "./ProductPicker";
import { useSelectOptions } from "./SearchBar";

export const LEAD_SOURCES = [
  { value: "marketing_response", label: "Marketing response", hint: LEAD_SOURCE_OPTIONS[0].meaning },
  { value: "web_form", label: "Web form", hint: LEAD_SOURCE_OPTIONS[1].meaning },
  { value: "referral", label: "Referral", hint: LEAD_SOURCE_OPTIONS[2].meaning },
  { value: "trade_show", label: "Trade show", hint: LEAD_SOURCE_OPTIONS[3].meaning },
  { value: "outbound", label: "Outbound", hint: LEAD_SOURCE_OPTIONS[4].meaning },
  { value: "partner", label: "Partner", hint: LEAD_SOURCE_OPTIONS[5].meaning },
  { value: "support", label: "Support", hint: LEAD_SOURCE_OPTIONS[6].meaning },
  { value: "other", label: "Other", hint: LEAD_SOURCE_OPTIONS[7].meaning },
] as const;

function leadSourceLabel(value: string): string {
  const known = LEAD_SOURCES.find((source) => source.value === value);
  if (known) return known.label;
  return value.split("_").join(" ").replace(/^\w/, (c) => c.toUpperCase());
}

export function leadDisplayName(lead: {
  first_name?: string | null;
  last_name?: string | null;
  lead_source?: string | null;
  notes?: string | null;
  company_name?: string | null;
}): string {
  const person = `${lead.first_name ?? ""} ${lead.last_name ?? ""}`.trim();
  if (person) return person;
  const source = LEAD_SOURCES.find((item) => item.value === lead.lead_source)?.label ?? lead.lead_source;
  if (source) return source;
  if (lead.notes?.trim()) {
    const snippet = lead.notes.trim();
    return snippet.length > 48 ? `${snippet.slice(0, 48)}…` : snippet;
  }
  return lead.company_name?.trim() || "Untitled lead";
}

export type LeadCaptureValues = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  company_id: string;
  rating: string;
  lead_source: string;
  lead_score: string;
  notes: string;
  product_ids: string[];
};

type PersonMatch = {
  id: string;
  kind: "contact" | "lead";
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_id: string;
  company_name: string;
  label: string;
};

const emptyForm = (overrides?: Partial<LeadCaptureValues>): LeadCaptureValues => ({
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company_name: "",
  company_id: "",
  rating: "warm",
  lead_source: "marketing_response",
  lead_score: "",
  notes: "",
  product_ids: [],
  ...overrides,
});

function contactMatch(person: Contact): PersonMatch {
  const email = person.work_email ?? person.email ?? person.personal_email ?? "";
  const name = `${person.first_name} ${person.last_name ?? ""}`.trim();
  return {
    id: person.id,
    kind: "contact",
    first_name: person.first_name,
    last_name: person.last_name ?? "",
    email,
    phone: person.phone || person.mobile || "",
    company_id: person.company_id,
    company_name: person.company_name ?? "",
    label: `${name}${email ? ` · ${email}` : ""}${person.company_name ? ` · ${person.company_name}` : ""}`,
  };
}

function leadMatch(lead: Lead): PersonMatch {
  const name = `${lead.first_name ?? ""} ${lead.last_name ?? ""}`.trim() || "Untitled lead";
  return {
    id: lead.id,
    kind: "lead",
    first_name: lead.first_name ?? "",
    last_name: lead.last_name ?? "",
    email: lead.email ?? "",
    phone: lead.phone ?? "",
    company_id: lead.company_id ?? "",
    company_name: lead.company_name ?? "",
    label: `${name}${lead.email ? ` · ${lead.email}` : ""}${lead.company_name ? ` · ${lead.company_name}` : ""}`,
  };
}

function SearchableLeadField({
  label,
  value,
  onChange,
  matches,
  onSelect,
  placeholder,
  tooltip,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  matches: PersonMatch[];
  onSelect: (match: PersonMatch) => void;
  placeholder?: string;
  tooltip?: string;
}) {
  return (
    <Stack gap={2}>
      <Input
        label={label}
        tooltip={tooltip}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
      {matches.length > 0 ? (
        <ul className="crm-lookup-list">
          {matches.map((match) => (
            <li key={`${match.kind}-${match.id}`}>
              <Button variant="ghost" onClick={() => onSelect(match)}>
                {match.label}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </Stack>
  );
}

export function LeadCaptureForm({
  orgId,
  lockedCompany,
  initial,
  submitLabel = "Create lead",
  submit,
  onCreated,
}: {
  orgId: string;
  lockedCompany?: { id: string; name: string };
  initial?: Partial<LeadCaptureValues>;
  submitLabel?: string;
  submit?: (body: LeadInput) => Promise<{ id: string; company_id?: string | null }>;
  onCreated: (result: { id: string; company_id?: string | null }) => void;
}) {
  const { show } = useToast();
  const leadSourceValues = useSelectOptions(
    orgId,
    "lead",
    "lead_source",
    LEAD_SOURCES.map((source) => source.value),
  );
  const [form, setForm] = useState(() =>
    emptyForm({
      company_id: lockedCompany?.id ?? "",
      company_name: lockedCompany?.name ?? "",
      ...initial,
    }),
  );
  const [duplicates, setDuplicates] = useState<DuplicateMatch | null>(null);
  const [people, setPeople] = useState<PersonMatch[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [linkedPerson, setLinkedPerson] = useState(false);
  const [lookupField, setLookupField] = useState<"first_name" | "last_name" | "email" | "phone" | "notes" | "lead_score" | null>(null);

  useEffect(() => {
    if (lockedCompany) {
      setForm((current) => ({
        ...current,
        company_id: lockedCompany.id,
        company_name: lockedCompany.name,
      }));
    }
  }, [lockedCompany?.id, lockedCompany?.name]);

  useEffect(() => {
    if (!form.email.trim() && !form.company_id) {
      setDuplicates(null);
      return;
    }
    const handle = window.setTimeout(() => {
      void crmApi
        .findLeadDuplicates(orgId, {
          company_id: form.company_id || undefined,
          email: form.email || undefined,
        })
        .then(setDuplicates)
        .catch(() => setDuplicates(null));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [orgId, form.email, form.company_id]);

  const lookupQuery = lookupField
    ? form[lookupField].trim()
    : "";

  useEffect(() => {
    // On a company screen the search is already scoped to that company's people, so one character is enough.
    const minChars = lockedCompany ? 1 : 2;
    if (linkedPerson || lookupQuery.length < minChars) {
      setPeople([]);
      return;
    }
    const handle = window.setTimeout(() => {
      void Promise.all([
        crmApi.listContacts(orgId, lockedCompany?.id, lookupQuery).catch(() => [] as Contact[]),
        crmApi.listLeads(orgId, undefined, lookupQuery, lockedCompany?.id).catch(() => [] as Lead[]),
      ]).then(([contacts, leads]) => {
        const seen = new Set<string>();
        const next: PersonMatch[] = [];
        for (const person of contacts) {
          const match = contactMatch(person);
          const key = match.email || match.label;
          if (seen.has(key)) continue;
          seen.add(key);
          next.push(match);
        }
        for (const lead of leads) {
          if (!lead.first_name && !lead.email && !lead.phone) continue;
          const match = leadMatch(lead);
          const key = match.email || match.label;
          if (seen.has(key)) continue;
          seen.add(key);
          next.push(match);
        }
        setPeople(next.slice(0, 8));
      });
    }, 250);
    return () => window.clearTimeout(handle);
  }, [orgId, lookupQuery, linkedPerson, lockedCompany?.id]);

  const applyMatch = (match: PersonMatch) => {
    setLinkedPerson(true);
    setPeople([]);
    setForm((current) => ({
      ...current,
      first_name: match.first_name || current.first_name,
      last_name: match.last_name || current.last_name,
      email: match.email || current.email,
      phone: match.phone || current.phone,
      company_id: lockedCompany?.id || match.company_id || current.company_id,
      company_name: lockedCompany?.name || match.company_name || current.company_name,
    }));
  };

  const canSubmit = Boolean(form.company_id.trim() || form.company_name.trim());
  const hasContact = Boolean(form.first_name.trim() || form.last_name.trim() || form.email.trim() || form.phone.trim());
  const openAtCompany = duplicates?.matches.length ?? 0;
  const matchesFor = (field: typeof lookupField) => (lookupField === field ? people : []);

  const handleCreate = async () => {
    if (!canSubmit) {
      setError(reportSaveBlocked(show, "Pick a company or type a company name."));
      return;
    }
    setSaving(true);
    setError(null);
    const body: LeadInput = {
      first_name: form.first_name.trim() || null,
      last_name: form.last_name.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      company_id: form.company_id || null,
      company_name: form.company_name.trim() || null,
      new_company_name: form.company_id ? null : form.company_name.trim() || null,
      rating: form.rating || "warm",
      lead_source: form.lead_source || null,
      lead_score: form.lead_score.trim() ? Number(form.lead_score) : null,
      notes: form.notes.trim() || null,
      product_ids: form.product_ids,
      lead_status: "new",
    };
    try {
      const res = await (submit ? submit(body) : crmApi.createLead(body, orgId));
      setForm(
        emptyForm({
          company_id: lockedCompany?.id ?? "",
          company_name: lockedCompany?.name ?? "",
          rating: "warm",
          lead_source: "marketing_response",
        }),
      );
      setLinkedPerson(false);
      show("Saved!", "success");
      onCreated(res);
    } catch (e) {
      setError(reportSaveFailure(show, e, "Failed to create lead"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap={3}>
      <p className="crm-help">
        Type a name, email, phone, source, notes, or company to search existing records.
        Choosing a match fills the linked details. If the contact or company is new, it is created when you save.
        Temperature is an estimate and is not searched.
      </p>

      {!lockedCompany && (
        <CompanyLookup
          orgId={orgId}
          value={form.company_name}
          companyId={form.company_id}
          allowCreate
          onChange={({ name, companyId }) => {
            setLinkedPerson(false);
            setForm({ ...form, company_name: name, company_id: companyId });
          }}
        />
      )}

      <FieldLabel htmlFor="lead-source" hint={FIELD_HINTS.lead_source}>
        Source
      </FieldLabel>
      <input
        id="lead-source-search"
        className="crm-select"
        list="lead-source-options"
        value={leadSourceLabel(form.lead_source)}
        onChange={(event) => {
          const typed = event.target.value;
          const match = leadSourceValues.find(
            (value) => leadSourceLabel(value).toLowerCase() === typed.toLowerCase() || value === typed,
          );
          setForm({ ...form, lead_source: match ?? typed });
        }}
        placeholder="Search or type a source"
        title={FIELD_HINTS.lead_source}
      />
      <datalist id="lead-source-options">
        {leadSourceValues.map((value) => (
          <option key={value} value={leadSourceLabel(value)} />
        ))}
      </datalist>

      <FieldLabel htmlFor="lead-temp" hint={FIELD_HINTS.lead_temperature}>
        Temperature (estimate)
      </FieldLabel>
      <select
        id="lead-temp"
        className="crm-select"
        value={form.rating}
        onChange={(e) => setForm({ ...form, rating: e.target.value })}
      >
        <option value="cold" title={LEAD_TEMPERATURE_OPTIONS[0].meaning}>
          Cold
        </option>
        <option value="warm" title={LEAD_TEMPERATURE_OPTIONS[1].meaning}>
          Warm
        </option>
        <option value="hot" title={LEAD_TEMPERATURE_OPTIONS[2].meaning}>
          Hot
        </option>
      </select>

      <SearchableLeadField
        label="Estimated score (0–100, optional)"
        tooltip={FIELD_HINTS.lead_score}
        value={form.lead_score}
        placeholder="e.g. 60"
        matches={matchesFor("lead_score")}
        onChange={(value) => {
          setLinkedPerson(false);
          setLookupField("lead_score");
          setForm({ ...form, lead_score: value });
        }}
        onSelect={applyMatch}
      />
      <SearchableLeadField
        label="What happened / notes"
        tooltip={FIELD_HINTS.lead_notes}
        value={form.notes}
        placeholder="e.g. Positive reply to March campaign — interested in demo"
        matches={matchesFor("notes")}
        onChange={(value) => {
          setLinkedPerson(false);
          setLookupField("notes");
          setForm({ ...form, notes: value });
        }}
        onSelect={applyMatch}
      />

      <h3 className="crm-section-title">Contact (optional)</h3>
      <SearchableLeadField
        label="First name"
        tooltip={FIELD_HINTS.lead_first_name}
        value={form.first_name}
        matches={matchesFor("first_name")}
        onChange={(value) => {
          setLinkedPerson(false);
          setLookupField("first_name");
          setForm({ ...form, first_name: value });
        }}
        onSelect={applyMatch}
      />
      <SearchableLeadField
        label="Last name"
        tooltip={FIELD_HINTS.lead_last_name}
        value={form.last_name}
        matches={matchesFor("last_name")}
        onChange={(value) => {
          setLinkedPerson(false);
          setLookupField("last_name");
          setForm({ ...form, last_name: value });
        }}
        onSelect={applyMatch}
      />
      <SearchableLeadField
        label="Email"
        tooltip={FIELD_HINTS.lead_email}
        value={form.email}
        placeholder="Start typing an email"
        matches={matchesFor("email")}
        onChange={(value) => {
          setLinkedPerson(false);
          setLookupField("email");
          setForm({ ...form, email: value });
        }}
        onSelect={applyMatch}
      />
      <SearchableLeadField
        label="Phone"
        tooltip={FIELD_HINTS.lead_phone}
        value={form.phone}
        matches={matchesFor("phone")}
        onChange={(value) => {
          setLinkedPerson(false);
          setLookupField("phone");
          setForm({ ...form, phone: value });
        }}
        onSelect={applyMatch}
      />      {!hasContact && <p className="crm-muted">No contact yet — this lead will be saved against the company only.</p>}
      {linkedPerson && <p className="crm-help">Linked to an existing person. Company, name, email, and phone were filled from that record.</p>}

      <ProductPicker
        orgId={orgId}
        value={form.product_ids}
        onChange={(product_ids: string[]) => setForm({ ...form, product_ids })}
      />

      {openAtCompany > 0 && (
        <p className="crm-help">
          This company already has {openAtCompany} open lead{openAtCompany === 1 ? "" : "s"}.
          {form.email.trim() ? " Matching email also found — review before saving." : ""}
        </p>
      )}
      {error && <p className="crm-error">{error}</p>}
      <div className="crm-form-actions">
        <Button disabled={!canSubmit || saving} onClick={() => handleCreate()}>
          {saving ? "Saving…" : submitLabel}
        </Button>
      </div>
    </Stack>
  );
}
